/**
 * Cotizador — Quotation module
 *
 * Routes:
 *   /cotizador         -> List + filters + create new
 *   /cotizador/:id     -> Edit or view
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useOutletContext, useSearchParams } from 'react-router-dom';
import api from '@/api/axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  FileText, Plus, MagnifyingGlass, Trash, FloppyDisk, Download,
  ArrowLeft, Calendar, User, Storefront, Tag, X, CheckCircle, PencilSimple, Eraser, WhatsappLogo, Eye,
} from '@phosphor-icons/react';

const STATES = {
  borrador: { label: 'Borrador', color: 'bg-slate-500/20 text-slate-300' },
  enviada: { label: 'Enviada', color: 'bg-blue-500/20 text-blue-400' },
  aprobada: { label: 'Aprobada', color: 'bg-emerald-500/20 text-emerald-400' },
  rechazada: { label: 'Rechazada', color: 'bg-red-500/20 text-red-400' },
  vencida: { label: 'Vencida', color: 'bg-orange-500/20 text-orange-400' },
  convertida: { label: 'Convertida', color: 'bg-purple-500/20 text-purple-400' },
};

const Q = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// =========================================================================
// LIST VIEW
// =========================================================================
const CotizadorList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (estado !== 'all') params.estado = estado;
      const { data } = await api.get('/cotizaciones', { params });
      setItems(data || []);
    } catch (e) {
      toast.error('Error al cargar cotizaciones');
    } finally {
      setLoading(false);
    }
  }, [q, estado]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4" data-testid="cotizador-list">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-[var(--text)] flex items-center gap-2">
            <FileText size={28} weight="duotone" className="text-[#1D4ED8]" />
            Cotizador
          </h1>
          <p className="text-xs text-[var(--text-muted)] mt-1">Crea, edita y exporta cotizaciones de Complementos y Herrajes, S.A.</p>
        </div>
        <Button onClick={() => navigate('/cotizador/nuevo')} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" data-testid="cot-new-btn">
          <Plus size={18} weight="bold" className="mr-1" /> Nueva cotización
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-3 bg-[var(--surface)] border-[var(--border)]">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por número, cliente, NIT, teléfono…"
              className="pl-9 bg-[var(--bg)] border-[var(--border)] text-[var(--text)]"
              data-testid="cot-search"
            />
          </div>
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger className="w-full sm:w-48 bg-[var(--bg)] border-[var(--border)] text-[var(--text)]" data-testid="cot-filter-estado">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {Object.entries(STATES).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* List */}
      {loading ? (
        <p className="text-center text-[var(--text-muted)] py-10">Cargando…</p>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center bg-[var(--surface)] border-[var(--border)]">
          <FileText size={48} weight="duotone" className="mx-auto text-[var(--text-faint)] mb-3" />
          <p className="text-[var(--text-muted)]">Sin cotizaciones todavía. Crea la primera.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(c => {
            const st = STATES[c.estado] || STATES.borrador;
            return (
              <Card
                key={c.id}
                className="p-4 bg-[var(--surface)] border-[var(--border)] hover:border-[#1D4ED8] cursor-pointer transition-colors"
                onClick={() => navigate(`/cotizador/${c.id}`)}
                data-testid={`cot-card-${c.numero}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">{c.numero}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold uppercase ${st.color}`}>{st.label}</span>
                </div>
                <h3 className="font-black text-[var(--text)] mb-0.5 truncate">{c.cliente?.nombre || '(sin cliente)'}</h3>
                <p className="text-xs text-[var(--text-muted)] truncate">{c.cliente?.nit || 'C/F'} · {c.cliente?.telefono || '—'}</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-[10px] text-[var(--text-faint)]">{(c.fecha || c.created_at || '').slice(0,10)}</span>
                  <span className="text-lg font-black text-emerald-600 dark:text-emerald-400">{Q(c.total)}</span>
                </div>
                <p className="text-[10px] text-[var(--text-faint)] mt-1">Por: {c.vendor_name}</p>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// =========================================================================
// EDIT / NEW VIEW
// =========================================================================
const CotizadorEdit = () => {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const outlet = useOutletContext() || {};
  const businesses = outlet.businesses || [];
  const isNew = routeId === 'nuevo';

  const cyhBiz = businesses?.find(b => /omplemento/i.test(b.name)) || outlet.selectedBusiness || businesses?.[0];

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [sendingWA, setSendingWA] = useState(false);
  const [form, setForm] = useState({
    id: null,
    numero: '',
    business_id: cyhBiz?.id || '',
    cliente: {
      nombre: searchParams.get('nombre') || '',
      nit: searchParams.get('nit') || 'C/F',
      telefono: searchParams.get('telefono') || '',
      direccion: searchParams.get('direccion') || '',
      email: searchParams.get('email') || '',
    },
    lineas: (() => {
      // Pre-fill line from Stock search if user clicked "Cotizar"
      try {
        const raw = sessionStorage.getItem('cotizador_prefill_line');
        if (raw) {
          sessionStorage.removeItem('cotizador_prefill_line');
          const parsed = JSON.parse(raw);
          return [parsed];
        }
      } catch { /* ignore */ }
      return [];
    })(),
    descuento_tipo: 'monto',
    descuento_valor: 0,
    condiciones_pago: '70% inicial al confirmar pedido, 30% contra entrega.',
    notas: '',
    validez_dias: 8,
    sucursal_id: 1,
    estado: 'borrador',
    firma_vendedor_url: null,
    firma_cliente_url: null,
    vendor_name: user?.name || 'Vendedor',
    productos_cotizados: [],
    empresa_emisora: 'cyh',     // "cyh" | "sa" | "sach"
  });

  const EMPRESAS_OPTIONS = [
    { id: 'cyh',  label: 'CYH — Complementos y Herrajes' },
    { id: 'sa',   label: 'SA — Soluciones Arquitectónicas' },
    { id: 'sach', label: 'Automatizaciones Sach' },
  ];

  const PRODUCT_CATEGORIES = [
    'Herrajes', 'Pasamanos', 'Vidrio templado', 'PVC', 'Aluminio',
    'Techo', 'Techo curvo', 'Tablayeso', 'Puertas automáticas', 'Reparación',
  ];

  const isAdminOrSup = ['admin', 'supervisor', 'gerente'].includes((user?.role || '').toLowerCase());

  const [sucursales, setSucursales] = useState([]);
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [unlocked, setUnlocked] = useState(false);  // overrides for vendor: precio y descuento >7%
  const [pinOpen, setPinOpen] = useState(false);
  const [pinValue, setPinValue] = useState('');
  const [overridePin, setOverridePin] = useState(null);  // PIN guardado para enviar al backend

  // Load existing or preview number
  useEffect(() => {
    (async () => {
      try {
        if (isNew) {
          const { data } = await api.get('/cotizaciones/next-number');
          setForm(f => ({ ...f, numero: data.numero, business_id: cyhBiz?.id || f.business_id }));
        } else {
          const { data } = await api.get(`/cotizaciones/${routeId}`);
          setForm(f => ({ ...f, ...data }));
        }
      } catch (e) {
        toast.error('No se pudo cargar la cotización');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // Refrescar el correlativo cuando cambia la empresa emisora (numeración por empresa)
  useEffect(() => {
    if (!isNew || loading) return;
    api.get(`/cotizaciones/next-number?empresa=${form.empresa_emisora || 'cyh'}`)
      .then(r => setForm(f => ({ ...f, numero: r.data.numero })))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.empresa_emisora]);

  // Load HarpERP sucursales
  useEffect(() => {
    api.get('/harperp/sucursales')
      .then(r => setSucursales(r.data?.data || []))
      .catch(() => setSucursales([]));
  }, []);

  // Compute totals
  const subtotal = (form.lineas || []).reduce((s, l) => s + (Number(l.cantidad || 0) * Number(l.precio_unitario || 0)), 0);
  const descAplicado = form.descuento_tipo === 'porcentaje'
    ? Math.min(subtotal, Math.max(0, subtotal * (Number(form.descuento_valor || 0) / 100)))
    : Math.min(subtotal, Math.max(0, Number(form.descuento_valor || 0)));
  const total = Math.max(0, subtotal - descAplicado);

  const setCliente = (k, v) => setForm(f => ({ ...f, cliente: { ...f.cliente, [k]: v } }));

  const addLinea = (l) => setForm(f => ({ ...f, lineas: [...f.lineas, l] }));
  const updateLinea = (i, patch) => setForm(f => ({ ...f, lineas: f.lineas.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  const removeLinea = (i) => setForm(f => ({ ...f, lineas: f.lineas.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.cliente?.nombre) { toast.error('Ingresa el nombre del cliente'); return; }
    if (form.lineas.length === 0) { toast.error('Agrega al menos un producto'); return; }
    setSaving(true);
    try {
      const payload = {
        business_id: form.business_id,
        cliente: form.cliente,
        lineas: form.lineas,
        descuento_tipo: form.descuento_tipo,
        descuento_valor: Number(form.descuento_valor || 0),
        condiciones_pago: form.condiciones_pago,
        notas: form.notas,
        validez_dias: Number(form.validez_dias || 8),
        sucursal_id: Number(form.sucursal_id) || null,
        estado: form.estado,
        firma_vendedor_url: form.firma_vendedor_url,
        firma_cliente_url: form.firma_cliente_url,
        vendor_name: form.vendor_name,
        empresa_emisora: form.empresa_emisora || 'cyh',
        tipo_cotizacion: undefined,
        productos_cotizados: form.productos_cotizados || [],
        override_pin: overridePin || undefined,
      };
      let saved;
      if (form.id) {
        const { data } = await api.put(`/cotizaciones/${form.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post('/cotizaciones', payload);
        saved = data;
        navigate(`/cotizador/${data.id}`, { replace: true });
      }
      setForm(f => ({ ...f, ...saved }));
      toast.success(`Cotización ${saved.numero} guardada`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (cotId, mode = 'download') => {
    /* mode: 'download' = open file via Web Share or download, 'preview' = open in iframe modal */
    try {
      const response = await api.get(`/cotizaciones/${cotId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (mode === 'preview') {
        setPreviewPdfUrl(url);
      } else {
        // Try Web Share API first (allows sharing the FILE, not a URL)
        try {
          const file = new File([blob], `Cotizacion_${form.numero || cotId}.pdf`, { type: 'application/pdf' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: `Cotización ${form.numero || cotId}` });
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            return;
          }
        } catch (e) { /* fallback to download */ }
        // Fallback: download as file
        const a = document.createElement('a');
        a.href = url;
        a.download = `Cotizacion_${form.numero || cotId}.pdf`;
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (e) {
      toast.error('Error al generar el PDF');
    }
  };

  const handleConvertir = async () => {
    if (!form.id) return;
    if (!window.confirm(`¿Convertir esta cotización en venta por ${Q(total)}?`)) return;
    try {
      await api.post(`/cotizaciones/${form.id}/convertir`);
      toast.success('Cotización convertida en venta');
      setForm(f => ({ ...f, estado: 'convertida' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al convertir');
    }
  };

  // Detecta si esta cotización es de Soluciones Arquitectónicas para mostrar botón OT
  // empresa_emisora se guarda como código corto: 'cyh' | 'sa' | 'sach'
  const isSolarBiz = (form.empresa_emisora || '').toLowerCase() === 'sa'
    || (form.business_name || '').toLowerCase().includes('soluciones arquitec');

  const [showGenOT, setShowGenOT] = React.useState(false);
  const [otAnticipo, setOtAnticipo] = React.useState({ monto: '', fecha: '', url: '', nombre: '' });
  const [otUploading, setOtUploading] = React.useState(false);
  const [otSubmitting, setOtSubmitting] = React.useState(false);

  const uploadOtDoc = async (file) => {
    if (!file) return;
    try {
      setOtUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/produccion/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setOtAnticipo(prev => ({ ...prev, url: r.data?.url, nombre: r.data?.nombre }));
    } catch (e) { toast.error('Error subiendo archivo'); }
    finally { setOtUploading(false); }
  };

  const handleGenerateOT = async () => {
    if (!form.id) return;
    if (!otAnticipo.monto || !otAnticipo.url) { toast.error('Anticipo + documento requeridos'); return; }
    try {
      setOtSubmitting(true);
      const r = await api.post(`/cotizaciones/${form.id}/generar-ot`, {
        anticipo_monto: Number(otAnticipo.monto),
        anticipo_fecha: otAnticipo.fecha || null,
        anticipo_documento_url: otAnticipo.url,
        anticipo_documento_nombre: otAnticipo.nombre,
      });
      toast.success(`OT ${r.data.numero} creada con ${r.data.materiales_prellenados} materiales prellenados`);
      setForm(f => ({ ...f, estado: 'convertida', ot_id: r.data.ot_id, ot_numero: r.data.numero }));
      setShowGenOT(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error generando OT');
    } finally { setOtSubmitting(false); }
  };

  const handleSendWhatsApp = async () => {
    if (!form.id) { toast.error('Primero guarda la cotización'); return; }
    if (!form.cliente?.telefono) { toast.error('Agrega el teléfono del cliente para enviar por WhatsApp'); return; }
    const tel = form.cliente.telefono;
    if (!window.confirm(`¿Enviar la cotización ${form.numero} por WhatsApp al cliente al número ${tel}?\n\nSe enviará el PDF como adjunto.`)) return;
    setSendingWA(true);
    try {
      const { data } = await api.post(`/cotizaciones/${form.id}/enviar-whatsapp`);
      toast.success(`Enviado a ${data.phone} ✅`);
      setForm(f => ({ ...f, estado: 'enviada' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al enviar por WhatsApp');
    } finally {
      setSendingWA(false);
    }
  };

  const handleValidatePin = async () => {
    if (!pinValue) { toast.error('Ingresa el PIN'); return; }
    try {
      const { data } = await api.post('/cotizaciones/validate-pin', { pin: pinValue });
      setUnlocked(true);
      setOverridePin(pinValue);
      setPinOpen(false);
      setPinValue('');
      toast.success(`Autorizado por ${data.authorized_by_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PIN incorrecto');
    }
  };

  // Cuando el vendedor escribe descuento > 7%, pedir PIN
  const handleDescuentoChange = (v) => {
    if (isAdminOrSup || unlocked) {
      setForm(f => ({ ...f, descuento_valor: v }));
      return;
    }
    const newVal = Number(v) || 0;
    let exceeds = false;
    if (form.descuento_tipo === 'porcentaje') {
      exceeds = newVal > 7;
    } else if (subtotal > 0) {
      exceeds = (newVal / subtotal) * 100 > 7;
    }
    if (exceeds) {
      setPinOpen(true);
      toast.info('Descuento mayor a 7% requiere autorización');
      return;
    }
    setForm(f => ({ ...f, descuento_valor: v }));
  };

  // Precios siempre libres (manual o HarpERP). Solo descuento > 7% pide PIN.
  const handlePriceClick = (i, value) => {
    updateLinea(i, { precio_unitario: value });
  };

  if (loading) return <div className="p-6 text-center text-[var(--text-muted)]">Cargando…</div>;

  const isLocked = form.estado === 'convertida';

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4" data-testid="cotizador-edit">
      {/* Topbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="outline" size="sm" onClick={() => navigate('/cotizador')} className="border-[var(--border)] shrink-0" data-testid="cot-back">
            <ArrowLeft size={16} weight="bold" /> <span className="ml-1 text-xs">Volver</span>
          </Button>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-black text-[var(--text)] truncate">
              Cotización <span className="text-[#1D4ED8]">{form.numero || '…'}</span>
            </h1>
            <p className="text-[10px] sm:text-xs text-[var(--text-muted)] truncate">Validez: {form.validez_dias} días · {form.vendor_name}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={form.estado} onValueChange={(v) => setForm(f => ({ ...f, estado: v }))} disabled={isLocked}>
            <SelectTrigger className="w-32 sm:w-36 bg-[var(--surface)] border-[var(--border)]" data-testid="cot-estado-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATES).filter(([k]) => k !== 'convertida').map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.id && form.estado !== 'convertida' && (
            <Button onClick={handleConvertir} className="bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm" data-testid="cot-convertir">
              <CheckCircle size={16} className="mr-1" /> <span className="hidden sm:inline">Convertir a venta</span><span className="sm:hidden">Convertir</span>
            </Button>
          )}
          {form.id && isSolarBiz && !form.ot_id && (
            <Button onClick={() => setShowGenOT(true)} className="bg-orange-600 hover:bg-orange-700 text-white text-xs sm:text-sm" data-testid="cot-generar-ot">
              🔨 <span className="hidden sm:inline">Generar OT Producción</span><span className="sm:hidden">Generar OT</span>
            </Button>
          )}
          {form.ot_id && (
            <a href={`/produccion`} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs sm:text-sm font-bold">
              OT {form.ot_numero} ↗
            </a>
          )}
          {!isAdminOrSup && (
            <Button
              variant={unlocked ? 'default' : 'outline'}
              size="sm"
              onClick={() => unlocked ? (setUnlocked(false), setOverridePin(null), toast.success('Edición bloqueada')) : setPinOpen(true)}
              className={unlocked ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'border-amber-500 text-amber-600 hover:bg-amber-50'}
              data-testid="cot-unlock-btn"
              title={unlocked ? 'Edición desbloqueada — click para bloquear' : 'Solicitar autorización para descuento > 7%'}
            >
              {unlocked ? '🔓 Desbloqueado' : '🔒 Autorizar'}
            </Button>
          )}
        </div>
      </div>

      {/* Cliente */}
      <Card className="p-4 bg-[var(--surface)] border-[var(--border)]">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text)] flex items-center gap-2">
            <User size={16} /> Datos del cliente
          </h2>
          <Button size="sm" variant="outline" onClick={() => setCustomerSearchOpen(true)} className="border-[var(--border)] text-xs" data-testid="cot-search-customer">
            <MagnifyingGlass size={14} className="mr-1" /> Buscar en CRM
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input placeholder="Nombre del cliente *" value={form.cliente.nombre} onChange={(e) => setCliente('nombre', e.target.value)} className="bg-[var(--bg)] border-[var(--border)]" data-testid="cot-cliente-nombre" />
          <Input placeholder="NIT (o C/F)" value={form.cliente.nit} onChange={(e) => setCliente('nit', e.target.value)} className="bg-[var(--bg)] border-[var(--border)]" data-testid="cot-cliente-nit" />
          <Input placeholder="Teléfono" value={form.cliente.telefono} onChange={(e) => setCliente('telefono', e.target.value)} className="bg-[var(--bg)] border-[var(--border)]" data-testid="cot-cliente-tel" />
          <Input placeholder="Email" value={form.cliente.email} onChange={(e) => setCliente('email', e.target.value)} className="bg-[var(--bg)] border-[var(--border)]" data-testid="cot-cliente-email" />
          <Input placeholder="Dirección" value={form.cliente.direccion} onChange={(e) => setCliente('direccion', e.target.value)} className="sm:col-span-2 bg-[var(--bg)] border-[var(--border)]" data-testid="cot-cliente-dir" />
        </div>
        <div className="mt-3">
          <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--text-muted)]">Productos cotizados * (selecciona uno o varios — se refleja en Mi Día)</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid="cot-productos-categorias">
            {PRODUCT_CATEGORIES.map(cat => {
              const active = (form.productos_cotizados || []).includes(cat);
              return (
                <label key={cat} className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors text-xs ${active ? 'bg-[#1D4ED8]/10 border-[#1D4ED8] text-[var(--text)] font-bold' : 'border-[var(--border)] text-[var(--text-muted)] hover:bg-[var(--surface-muted)]'}`}>
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={(e) => {
                      const list = new Set(form.productos_cotizados || []);
                      if (e.target.checked) list.add(cat); else list.delete(cat);
                      setForm(f => ({ ...f, productos_cotizados: Array.from(list) }));
                    }}
                    className="accent-[#1D4ED8]"
                    data-testid={`cat-${cat.replace(/\s/g,'-').toLowerCase()}`}
                  />
                  {cat}
                </label>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Sucursal */}
      <Card className="p-4 bg-[var(--surface)] border-[var(--border)]">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--text-muted)]">Sucursal CYH</label>
            <Select value={String(form.sucursal_id || 1)} onValueChange={(v) => setForm(f => ({ ...f, sucursal_id: Number(v) }))}>
              <SelectTrigger className="bg-[var(--bg)] border-[var(--border)] mt-1" data-testid="cot-sucursal">
                <SelectValue placeholder="Selecciona" />
              </SelectTrigger>
              <SelectContent>
                {sucursales.map(s => (<SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--text-muted)]">Validez (días)</label>
            <Input type="number" value={form.validez_dias} onChange={(e) => setForm(f => ({ ...f, validez_dias: e.target.value }))} className="bg-[var(--bg)] border-[var(--border)] mt-1" data-testid="cot-validez" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--text-muted)]">Fecha</label>
            <Input value={(form.fecha || new Date().toISOString()).slice(0,10)} disabled className="bg-[var(--surface-muted)] border-[var(--border)] mt-1 opacity-80" />
          </div>
        </div>
      </Card>

      {/* Líneas */}
      <Card className="p-4 bg-[var(--surface)] border-[var(--border)]">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text)] flex items-center gap-2">
            <Tag size={16} /> Productos / Servicios
          </h2>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setProductSearchOpen(true)} className="border-[var(--border)]" data-testid="cot-search-product">
              <MagnifyingGlass size={14} className="mr-1" /> Buscar HarpERP
            </Button>
            <Button size="sm" onClick={() => addLinea({ descripcion: '', cantidad: 1, precio_unitario: 0, color: '', medida: '' })} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" data-testid="cot-add-manual">
              <Plus size={14} className="mr-1" /> Línea manual
            </Button>
          </div>
        </div>

        {form.lineas.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] text-center py-6 italic">Agrega productos buscando en HarpERP o manualmente.</p>
        ) : (
          <div className="space-y-2">
            {/* Desktop header */}
            <div className="hidden md:grid grid-cols-[40px,90px,1fr,100px,100px,120px,120px,40px] gap-2 px-2 py-1 text-[10px] uppercase font-bold tracking-wider text-[var(--text-muted)]">
              <span>#</span><span>Cant.</span><span>Descripción</span><span>Color</span><span>Medida</span><span className="text-right">P. Unit.</span><span className="text-right">Total</span><span></span>
            </div>
            {form.lineas.map((l, i) => {
              const totalLinea = (Number(l.cantidad) || 0) * (Number(l.precio_unitario) || 0);
              return (
                <div key={i} className="border border-[var(--border)] rounded-md p-2 bg-[var(--bg)]">
                  <div className="hidden md:grid grid-cols-[40px,90px,1fr,100px,100px,120px,120px,40px] gap-2 items-center">
                    <span className="text-xs font-bold text-[var(--text-muted)]">{i + 1}</span>
                    <Input type="number" value={l.cantidad} onChange={(e) => updateLinea(i, { cantidad: e.target.value })} className="h-9 bg-[var(--surface)] border-[var(--border)]" />
                    <Input value={l.descripcion} onChange={(e) => updateLinea(i, { descripcion: e.target.value })} placeholder="Descripción" className="h-9 bg-[var(--surface)] border-[var(--border)]" />
                    <Input value={l.color || ''} onChange={(e) => updateLinea(i, { color: e.target.value })} placeholder="Color" className="h-9 bg-[var(--surface)] border-[var(--border)]" />
                    <Input value={l.medida || ''} onChange={(e) => updateLinea(i, { medida: e.target.value })} placeholder="Medida" className="h-9 bg-[var(--surface)] border-[var(--border)]" />
                    <Input type="number" value={l.precio_unitario}
                      onChange={(e) => handlePriceClick(i, e.target.value)}
                      className="h-9 bg-[var(--surface)] border-[var(--border)] text-right" />
                    <span className="text-right font-black text-[var(--text)]">{Q(totalLinea)}</span>
                    <button onClick={() => removeLinea(i)} className="text-red-500 hover:text-red-700"><Trash size={16} /></button>
                  </div>
                  {/* Mobile stacked */}
                  <div className="md:hidden space-y-2">
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-bold text-[var(--text-muted)]">#{i + 1}</span>
                      <button onClick={() => removeLinea(i)} className="text-red-500 text-xs"><Trash size={14} /></button>
                    </div>
                    <Input value={l.descripcion} onChange={(e) => updateLinea(i, { descripcion: e.target.value })} placeholder="Descripción" className="bg-[var(--surface)] border-[var(--border)]" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={l.color || ''} onChange={(e) => updateLinea(i, { color: e.target.value })} placeholder="Color" className="bg-[var(--surface)] border-[var(--border)]" />
                      <Input value={l.medida || ''} onChange={(e) => updateLinea(i, { medida: e.target.value })} placeholder="Medida" className="bg-[var(--surface)] border-[var(--border)]" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div><span className="text-[9px] uppercase font-bold text-[var(--text-muted)]">Cantidad</span><Input type="number" inputMode="numeric" value={l.cantidad} onChange={(e) => updateLinea(i, { cantidad: e.target.value })} className="bg-[var(--surface)] border-[var(--border)]" /></div>
                      <div><span className="text-[9px] uppercase font-bold text-[var(--text-muted)]">P.Unit</span>
                        <Input type="number" inputMode="numeric" value={l.precio_unitario}
                          onChange={(e) => handlePriceClick(i, e.target.value)}
                          className="bg-[var(--surface)] border-[var(--border)]" /></div>
                      <div><span className="text-[9px] uppercase font-bold text-[var(--text-muted)]">Total</span><div className="px-2 py-2 text-sm font-black text-emerald-600">{Q(totalLinea)}</div></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Totals */}
        <div className="mt-4 flex flex-col sm:flex-row sm:justify-end gap-3">
          <div className="bg-[var(--bg)] border border-[var(--border)] rounded-md p-3 w-full sm:w-80 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-[var(--text-muted)]">Subtotal</span><span className="font-bold text-[var(--text)]">{Q(subtotal)}</span></div>
            <div className="flex items-center gap-2">
              <Select value={form.descuento_tipo} onValueChange={(v) => setForm(f => ({ ...f, descuento_tipo: v }))}>
                <SelectTrigger className="h-8 w-24 text-xs bg-[var(--surface)] border-[var(--border)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monto">Q</SelectItem>
                  <SelectItem value="porcentaje">%</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" value={form.descuento_valor} onChange={(e) => handleDescuentoChange(e.target.value)} className="h-8 bg-[var(--surface)] border-[var(--border)]" data-testid="cot-descuento" />
              <span className="text-xs text-red-500 whitespace-nowrap">-{Q(descAplicado)}</span>
            </div>
            <div className="flex justify-between border-t border-[var(--border)] pt-2 mt-2"><span className="font-black text-[var(--text)]">TOTAL</span><span className="font-black text-lg text-emerald-600 dark:text-emerald-400" data-testid="cot-total">{Q(total)}</span></div>
          </div>
        </div>
      </Card>

      {/* Conditions + notes */}
      <Card className="p-4 bg-[var(--surface)] border-[var(--border)]">
        <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text)] mb-3">Empresa emisora</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
          {EMPRESAS_OPTIONS.map(opt => {
            const active = (form.empresa_emisora || 'cyh') === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setForm(f => ({ ...f, empresa_emisora: opt.id }))}
                className={`text-left p-3 rounded-lg border-2 transition-all ${active ? 'border-[#1D4ED8] bg-blue-50 dark:bg-blue-950/30 shadow-sm' : 'border-[var(--border)] bg-[var(--bg)] hover:border-[var(--text-muted)]'}`}
                data-testid={`emisor-${opt.id}`}
              >
                <div className="text-xs font-bold text-[var(--text)]">{opt.label}</div>
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-[var(--text-faint)] italic mb-4">
          El logo de Grupo Sach siempre va arriba. Debajo, el PDF muestra el logo de la empresa emisora seleccionada y su NIT correspondiente.
        </p>

        <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text)] mb-3">Condiciones</h2>
        <Textarea value={form.condiciones_pago} onChange={(e) => setForm(f => ({ ...f, condiciones_pago: e.target.value }))} rows={2} className="bg-[var(--bg)] border-[var(--border)] mb-3" data-testid="cot-condiciones" />
        <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text)] mb-2">Notas (opcional)</h2>
        <Textarea value={form.notas} onChange={(e) => setForm(f => ({ ...f, notas: e.target.value }))} rows={2} className="bg-[var(--bg)] border-[var(--border)]" data-testid="cot-notas" />
      </Card>

      {/* Signatures - vendedor y cliente */}
      <Card className="p-4 bg-[var(--surface)] border-[var(--border)]">
        <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text)] mb-3">Firmas</h2>
        <div className="mb-3">
          <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--text-muted)]">Nombre del vendedor</label>
          <Input
            value={form.vendor_name}
            onChange={(e) => setForm(f => ({ ...f, vendor_name: e.target.value }))}
            placeholder="Nombre del vendedor que aparece en el PDF"
            className="bg-[var(--bg)] border-[var(--border)] mt-1"
            data-testid="cot-vendor-name"
          />
          <p className="text-[10px] text-[var(--text-faint)] mt-1 italic">Cámbialo si la cotización es a nombre de otro vendedor.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SignaturePad
            label={`Firma vendedor — ${form.vendor_name || '(sin nombre)'}`}
            value={form.firma_vendedor_url}
            onChange={(v) => setForm(f => ({ ...f, firma_vendedor_url: v }))}
            testid="cot-firma-vendedor"
          />
          <SignaturePad
            label={`Firma cliente — ${form.cliente.nombre || '(sin nombre)'}`}
            value={form.firma_cliente_url}
            onChange={(v) => setForm(f => ({ ...f, firma_cliente_url: v }))}
            testid="cot-firma-cliente"
          />
        </div>
        <p className="text-[10px] text-[var(--text-faint)] mt-2 italic">Las firmas son opcionales. Si las dejas en blanco, el PDF mostrará una línea para firmar a mano.</p>
      </Card>

      {/* Sticky save bar — usa sticky (no fixed) para que en móvil no rompa el scroll del teclado iOS */}
      <div className="sticky bottom-0 left-0 right-0 -mx-4 sm:-mx-6 bg-[var(--surface)]/95 backdrop-blur border-t border-[var(--border)] p-3 shadow-lg z-30">
        <div className="flex flex-wrap gap-2 justify-end max-w-6xl mx-auto">
          {form.id && (
            <Button variant="outline" onClick={() => downloadPdf(form.id, 'preview')} className="border-[#1D4ED8] text-[#1D4ED8] hover:bg-[#1D4ED8]/10 text-xs sm:text-sm flex-1 sm:flex-none" data-testid="cot-preview">
              <Eye size={16} className="mr-1" /> Visualizar
            </Button>
          )}
          {form.id && (
            <Button variant="outline" onClick={() => downloadPdf(form.id, 'download')} className="border-[var(--border)] text-xs sm:text-sm flex-1 sm:flex-none" data-testid="cot-download-pdf">
              <Download size={16} className="mr-1" /> PDF
            </Button>
          )}
          {form.id && form.cliente?.telefono && (
            <Button onClick={handleSendWhatsApp} disabled={sendingWA || isLocked} className="bg-[#25D366] hover:bg-[#1FAB52] text-white text-xs sm:text-sm flex-1 sm:flex-none" data-testid="cot-send-whatsapp">
              <WhatsappLogo size={16} weight="bold" className="mr-1" /> {sendingWA ? '…' : 'WhatsApp'}
            </Button>
          )}
          <Button onClick={() => handleSave()} disabled={saving || isLocked} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white text-xs sm:text-sm flex-1 sm:flex-none" data-testid="cot-save">
            <FloppyDisk size={16} className="mr-1" /> {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </div>

      {/* Product search dialog */}
      {productSearchOpen && (
        <ProductSearch
          sucursalId={form.sucursal_id}
          onClose={() => setProductSearchOpen(false)}
          onPick={(p) => {
            addLinea({
              sku: p.sku,
              descripcion: p.descripcion,
              cantidad: 1,
              precio_unitario: Number(p.precio || 0),
              color: '',
              medida: p.medida || '',
              imagen: p.imagen || null,
              harperp_id: p.id || null,
            });
            setProductSearchOpen(false);
            toast.success(`Agregado: ${p.sku}`);
          }}
        />
      )}

      {/* Customer search dialog */}
      {customerSearchOpen && (
        <CustomerSearch
          onClose={() => setCustomerSearchOpen(false)}
          onPick={(c) => {
            setForm(f => ({ ...f, cliente: { ...f.cliente, ...c } }));
            setCustomerSearchOpen(false);
            toast.success(`Cliente seleccionado: ${c.nombre}`);
          }}
        />
      )}

      {/* PDF preview modal */}
      {previewPdfUrl && (
        <Dialog open onOpenChange={() => { URL.revokeObjectURL(previewPdfUrl); setPreviewPdfUrl(null); }}>
          <DialogContent className="max-w-4xl w-[96vw] h-[90vh] p-0 bg-[var(--bg)] border-[var(--border)] flex flex-col" data-testid="pdf-preview-dialog">
            <DialogHeader className="px-4 py-2 border-b border-[var(--border)] flex flex-row items-center justify-between space-y-0">
              <DialogTitle className="text-sm sm:text-base">Vista previa — {form.numero}</DialogTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={async () => {
                  // Intentar Web Share API con FILE (asi NO incluye el link, solo el archivo)
                  try {
                    const res = await fetch(previewPdfUrl);
                    const blob = await res.blob();
                    const file = new File([blob], `Cotizacion_${form.numero}.pdf`, { type: 'application/pdf' });
                    if (navigator.canShare && navigator.canShare({ files: [file] })) {
                      await navigator.share({ files: [file], title: `Cotización ${form.numero}` });
                      return;
                    }
                  } catch (e) { /* fallback */ }
                  // Fallback: descarga directa
                  const a = document.createElement('a');
                  a.href = previewPdfUrl;
                  a.download = `Cotizacion_${form.numero}.pdf`;
                  a.target = '_blank';
                  a.rel = 'noopener';
                  document.body.appendChild(a); a.click(); a.remove();
                }} className="border-[var(--border)]" data-testid="preview-download">
                  <Download size={14} className="mr-1" /> Descargar
                </Button>
                {form.cliente?.telefono && (
                  <Button size="sm" onClick={() => { URL.revokeObjectURL(previewPdfUrl); setPreviewPdfUrl(null); handleSendWhatsApp(); }} className="bg-[#25D366] hover:bg-[#1FAB52] text-white" data-testid="preview-whatsapp">
                    <WhatsappLogo size={14} weight="bold" className="mr-1" /> Enviar
                  </Button>
                )}
              </div>
            </DialogHeader>
            <iframe src={previewPdfUrl} title="Cotización PDF" className="flex-1 w-full border-0" data-testid="pdf-preview-iframe" />
          </DialogContent>
        </Dialog>
      )}

      {/* PIN modal — autorización para vendedores */}
      {pinOpen && (
        <Dialog open onOpenChange={(o) => !o && setPinOpen(false)}>
          <DialogContent className="max-w-md bg-[var(--bg)] border-[var(--border)]" data-testid="pin-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">🔒 Autorización requerida</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[var(--text-muted)]">Ingresa el PIN de un supervisor para autorizar:</p>
            <ul className="text-xs text-[var(--text-muted)] list-disc pl-5 space-y-1">
              <li>Modificar precios unitarios</li>
              <li>Aplicar descuentos mayores al 7%</li>
            </ul>
            <Input
              type="password"
              value={pinValue}
              onChange={(e) => setPinValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleValidatePin()}
              placeholder="PIN…"
              className="bg-[var(--surface)] border-[var(--border)]"
              autoFocus
              data-testid="pin-input"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setPinOpen(false); setPinValue(''); }} className="border-[var(--border)]" data-testid="pin-cancel">Cancelar</Button>
              <Button onClick={handleValidatePin} className="bg-[#1D4ED8] text-white" data-testid="pin-confirm">Autorizar</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal: Generar OT desde cotización */}
      {showGenOT && (
        <Dialog open onOpenChange={(o) => !o && setShowGenOT(false)}>
          <DialogContent className="bg-[var(--bg)] border-[var(--border)] text-[var(--text)] max-w-md" data-testid="gen-ot-dialog">
            <DialogHeader>
              <DialogTitle>🔨 Generar OT de Producción</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-[var(--text-muted)]">Se creará una OT en Producción con todos los datos de esta cotización pre-llenados, incluyendo materiales como "cotizados". Requiere registro del anticipo con documento.</p>
            <div className="space-y-2">
              <div>
                <label className="text-[10px] uppercase text-[var(--text-muted)]">Monto anticipo (Q)</label>
                <Input type="number" value={otAnticipo.monto} onChange={(e) => setOtAnticipo({ ...otAnticipo, monto: e.target.value })} placeholder="0.00" className="bg-[var(--surface)] border-[var(--border)] text-sm" data-testid="ot-anticipo-monto" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-[var(--text-muted)]">Fecha</label>
                <Input type="date" value={otAnticipo.fecha} onChange={(e) => setOtAnticipo({ ...otAnticipo, fecha: e.target.value })} className="bg-[var(--surface)] border-[var(--border)] text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-[var(--text-muted)]">Documento (factura/recibo)</label>
                <Input type="file" accept="image/*,application/pdf" disabled={otUploading} onChange={(e) => uploadOtDoc(e.target.files?.[0])} className="text-xs bg-[var(--surface)] border-[var(--border)]" data-testid="ot-anticipo-file" />
                {otAnticipo.url && <p className="text-[10px] text-emerald-400">📎 {otAnticipo.nombre}</p>}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowGenOT(false)} className="border-[var(--border)]">Cancelar</Button>
              <Button onClick={handleGenerateOT} disabled={otSubmitting || !otAnticipo.url} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="ot-confirm">
                {otSubmitting ? 'Creando…' : 'Crear OT'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// =========================================================================
// CUSTOMER SEARCH MODAL (busca en CRM customers + sales_activities)
// =========================================================================
const CustomerSearch = ({ onClose, onPick }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async () => {
    if (!q.trim()) { setItems([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/cotizaciones/search-customers', { params: { q } });
      setItems(data?.data || []);
    } catch (e) {
      toast.error('Error al buscar clientes');
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(search, 350);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[96vw] sm:w-auto max-w-2xl bg-[var(--bg)] border-[var(--border)] text-[var(--text)] max-h-[90vh] overflow-y-auto" data-testid="customer-search-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><User size={20} /> Buscar cliente en CRM</DialogTitle>
        </DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, teléfono o NIT…" className="bg-[var(--surface)] border-[var(--border)]" autoFocus data-testid="customer-search-input" />
        {loading ? (
          <p className="text-center py-6 text-[var(--text-muted)]">Buscando…</p>
        ) : items.length === 0 ? (
          <p className="text-center py-6 text-[var(--text-muted)] italic text-xs">
            {q ? 'Sin resultados.' : 'Escribe el nombre, teléfono o NIT del cliente.'}
          </p>
        ) : (
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {items.map((c, i) => (
              <button
                key={i}
                onClick={() => onPick(c)}
                className="w-full text-left p-2 rounded border border-[var(--border)] hover:border-[#1D4ED8] hover:bg-[var(--surface)] transition-colors"
                data-testid={`customer-row-${i}`}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-sm text-[var(--text)] truncate">{c.nombre}</p>
                    <p className="text-[10px] text-[var(--text-muted)]">
                      NIT {c.nit} · Tel {c.telefono || '—'}
                    </p>
                    {c.direccion && <p className="text-[10px] text-[var(--text-faint)] truncate">{c.direccion}</p>}
                  </div>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--surface-muted)] text-[var(--text-muted)] uppercase">{c.source}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// =========================================================================
// PRODUCT SEARCH MODAL
// =========================================================================
const ProductSearch = ({ sucursalId, onClose, onPick }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const search = useCallback(async (resetPage = true) => {
    setLoading(true);
    try {
      const params = { q: q || undefined, page: resetPage ? 1 : page, per_page: 25 };
      if (sucursalId) params.sucursal_id = sucursalId;
      const { data } = await api.get('/harperp/productos', { params });
      setItems(data?.data || []);
      setTotal(data?.paginacion?.total || 0);
      if (resetPage) setPage(1);
    } catch (e) {
      toast.error('Error al buscar en HarpERP');
    } finally {
      setLoading(false);
    }
  }, [q, page, sucursalId]);

  useEffect(() => { search(true); /* eslint-disable-next-line */ }, []);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[96vw] sm:w-auto max-w-3xl bg-[var(--bg)] border-[var(--border)] text-[var(--text)] max-h-[90vh] overflow-y-auto" data-testid="product-search-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Storefront size={20} /> Buscar producto en HarpERP</DialogTitle>
        </DialogHeader>
        <div className="flex gap-2 mb-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search(true)} placeholder="Buscar por SKU o descripción…" className="bg-[var(--surface)] border-[var(--border)]" autoFocus data-testid="product-search-input" />
          <Button onClick={() => search(true)} className="bg-[#1D4ED8] text-white"><MagnifyingGlass size={16} /></Button>
        </div>
        {loading ? (
          <p className="text-center py-6 text-[var(--text-muted)]">Buscando…</p>
        ) : items.length === 0 ? (
          <p className="text-center py-6 text-[var(--text-muted)] italic">Sin resultados.</p>
        ) : (
          <>
            <p className="text-xs text-[var(--text-muted)] mb-2">{total} productos encontrados — mostrando {items.length}</p>
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {items.map(p => (
                <button
                  key={p.id || p.sku}
                  onClick={() => onPick(p)}
                  className="w-full text-left p-2 rounded border border-[var(--border)] hover:border-[#1D4ED8] hover:bg-[var(--surface)] transition-colors"
                  data-testid={`product-row-${p.sku}`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-black text-xs text-[#1D4ED8]">{p.sku}</span>
                        {p.categoria && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--surface-muted)] text-[var(--text-muted)] uppercase">{p.categoria}</span>}
                      </div>
                      <p className="text-sm text-[var(--text)] truncate">{p.descripcion}</p>
                      <p className="text-[10px] text-[var(--text-muted)]">{p.medida || '—'} · Stock: {p.existencia_total}</p>
                    </div>
                    <span className="font-black text-emerald-600 dark:text-emerald-400 whitespace-nowrap">{Q(p.precio)}</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// =========================================================================
// SIGNATURE PAD (canvas)
// =========================================================================
const SignaturePad = ({ label, value, onChange, testid }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(!!value);

  // Load existing signature
  useEffect(() => {
    if (value && canvasRef.current) {
      const c = canvasRef.current;
      const ctx = c.getContext('2d');
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, c.width, c.height);
      img.src = value;
      setHasContent(true);
    }
  }, [value]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return {
      x: ((t?.clientX ?? e.clientX) - rect.left) * (c.width / rect.width),
      y: ((t?.clientY ?? e.clientY) - rect.top) * (c.height / rect.height),
    };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    setHasContent(true);
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onChange(dataUrl);
  };

  const clear = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setHasContent(false);
    onChange(null);
  };

  return (
    <div data-testid={testid} style={{ overflowAnchor: 'none' }}>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--text-muted)]">{label}</label>
        {hasContent && (
          <button type="button" onClick={clear} className="text-[10px] text-red-500 hover:text-red-700 flex items-center gap-1">
            <Eraser size={11} /> Limpiar
          </button>
        )}
      </div>
      <div className="border border-dashed border-[var(--border)] rounded bg-white">
        <canvas
          ref={canvasRef}
          width={400}
          height={120}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          tabIndex={-1}
          className="w-full h-32 cursor-crosshair touch-none rounded outline-none"
          style={{ touchAction: 'none' }}
        />
      </div>
      {!hasContent && <p className="text-[9px] text-[var(--text-faint)] mt-1 italic flex items-center gap-1"><PencilSimple size={10} /> Firma con el dedo (móvil) o mouse</p>}
    </div>
  );
};

// =========================================================================
// EXPORTS
// =========================================================================
export const Cotizador = () => {
  const { id } = useParams();
  return id ? <CotizadorEdit /> : <CotizadorList />;
};

export default Cotizador;
